import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "@phosphor-icons/react";
import { livePhotoUrl } from "@/lib/fmUtils";
import { useAuth } from "@/contexts/AuthContext";

const LAYOUT = "main products";

const fmtPrice = (v) => {
  const n = Number(v);
  return v === "" || v == null || Number.isNaN(n) ? "-" : `$${n.toFixed(2)}`;
};

const productPriceLabel = (variants) => {
  const prices = (variants || [])
    .map((v) => Number(v["product::Price"]))
    .filter((n) => !Number.isNaN(n) && n > 0);
  if (!prices.length) return "-";
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? `$${min.toFixed(2)}` : `$${min.toFixed(2)} - $${max.toFixed(2)}`;
};

export default function ProductGridPrint() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [printed, setPrinted] = useState(false);
  const autoPrintTimer = useRef(null);

  useEffect(() => {
    if (user && user.role === "customer") navigate("/", { replace: true });
  }, [user, navigate]);

  const records = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("productGridPrintRecords") || "[]");
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    if (!records.length || printed) return;
    autoPrintTimer.current = setTimeout(() => {
      setPrinted(true);
      window.print();
    }, 2500);
    return () => clearTimeout(autoPrintTimer.current);
  }, [records, printed]);

  return (
    <div className="min-h-screen bg-white" data-testid="product-grid-print-page">
      <div className="flex items-center justify-between border-b border-zinc-300 px-6 py-3 print:hidden">
        <Button
          variant="outline"
          size="sm"
          className="rounded-sm border-zinc-300"
          onClick={() => navigate("/")}
          data-testid="print-back-button"
        >
          <ArrowLeft size={14} className="mr-1.5" /> Back
        </Button>
        <span className="text-sm text-zinc-500">{records.length} product(s)</span>
        <Button
          size="sm"
          className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
          onClick={() => window.print()}
          data-testid="print-now-button"
        >
          <Printer size={14} className="mr-1.5" /> Print
        </Button>
      </div>

      {records.length === 0 ? (
        <div className="flex h-64 items-center justify-center text-sm text-zinc-500" data-testid="print-empty-state">
          No products selected. Go back and select products first.
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4 p-6 print:gap-3 print:p-2">
          {records.map((rec) => {
            const fd = rec.fieldData || {};
            const variants = rec.portalData?.product || [];
            const src = fd.photo ? livePhotoUrl(LAYOUT, rec.recordId, "photo") : null;
            return (
              <div
                key={rec.recordId}
                className="flex flex-col overflow-hidden rounded-sm border border-zinc-300 break-inside-avoid"
                style={{ pageBreakInside: "avoid" }}
                data-testid={`print-card-${rec.recordId}`}
              >
                <div className="flex aspect-square items-center justify-center border-b border-zinc-200 bg-zinc-50">
                  {src ? (
                    <img src={src} alt={fd["item no"]} className="h-full w-full object-contain" />
                  ) : (
                    <span className="text-xs text-zinc-400">No photo</span>
                  )}
                </div>
                <div className="flex flex-col gap-0.5 p-2">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-bold text-zinc-900">{fd["item no"] || "-"}</span>
                    <span className="text-sm font-semibold text-zinc-900">{productPriceLabel(variants)}</span>
                  </div>
                  <div className="text-xs text-zinc-600">{fd.description || "-"}</div>
                  {variants.length > 0 && (
                    <table className="mt-1 w-full border-t border-zinc-200 text-[11px]">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-[0.03em] text-zinc-400">
                          <th className="pt-1 text-left font-normal">Size</th>
                          <th className="pt-1 text-right font-normal">Pcs/Ctn</th>
                          <th className="pt-1 text-right font-normal">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {variants.map((v) => (
                          <tr key={v.recordId} className="border-t border-zinc-100 text-zinc-700">
                            <td className="py-0.5">{(v["product::Size"] || "").trim() || "-"}</td>
                            <td className="py-0.5 text-right">
                              {v["product::装箱率"] ? Math.round(Number(v["product::装箱率"])) : "-"}
                            </td>
                            <td className="py-0.5 text-right">{fmtPrice(v["product::Price"])}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
